import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { CalendarDays, MapPin, CreditCard, Paperclip, Crown, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  pending: "bg-yellow-500/15 text-yellow-700 border-yellow-500/30",
  confirmed: "bg-[#C5A059]/15 text-[#8B6D2E] border-[#C5A059]/40",
  in_progress: "bg-blue-500/15 text-blue-700 border-blue-500/30",
  completed: "bg-green-500/15 text-green-700 border-green-500/30",
  cancelled: "bg-red-500/15 text-red-700 border-red-500/30",
};

const PAYMENT_STATUS_LABEL = {
  unpaid: "Unpaid",
  deposit_paid: "Deposit paid",
  paid: "Paid in full",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).finally(() => setLoading(false));
    api.get("/memberships/me").then((r) => setMembership(r.data)).catch(() => {});
  }, []);

  const copyCode = () => {
    if (!membership?.membership_code) return;
    navigator.clipboard.writeText(membership.membership_code);
    toast.success("Code copied");
  };

  const cancelSub = async () => {
    if (!window.confirm("Cancel your membership at the end of the current billing period?")) return;
    try {
      await api.post("/memberships/cancel");
      toast.success("Membership will end at period close");
      const { data } = await api.get("/memberships/me");
      setMembership(data);
    } catch (e) {
      toast.error("Could not cancel. Please try again.");
    }
  };

  const reactivateSub = async () => {
    try {
      await api.post("/memberships/reactivate");
      toast.success("Membership renewal restored");
      const { data } = await api.get("/memberships/me");
      setMembership(data);
    } catch (e) {
      toast.error("Could not reactivate.");
    }
  };

  return (
    <div data-testid="dashboard-page" className="bg-[#F5EFE4] min-h-[80vh]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <p className="overline">Client Portal</p>
            <h1 className="font-serif text-5xl lg:text-6xl mt-4 text-[#1A1815]">Welcome, {user?.name?.split(" ")[0] || "Guest"}.</h1>
            <p className="text-[#6B665E] mt-3">A discreet ledger of your reservations.</p>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <Link to="/book" className="luxor-btn-primary" data-testid="dashboard-new-booking"><CalendarDays size={14} /> New Reservation</Link>
          </div>
        </div>
        <div className="mt-14">
          {membership?.active ? (
            <div className="bg-[#1A1815] text-[#F5EFE4] p-6 md:p-8 mb-10 flex flex-col gap-4" data-testid="dashboard-membership-card">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C5A059] flex items-center justify-center text-white"><Crown size={20} /></div>
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">Active Membership</p>
                    <h3 className="font-serif text-2xl mt-1">{membership.details?.name} · {membership.discount_percent}% off</h3>
                    {membership.cancel_at_period_end ? (
                      <p className="text-xs text-[#F5EFE4]/70 mt-1">Ends at end of billing period · <span className="text-[#C5A059]">reactivate below</span></p>
                    ) : (
                      <p className="text-xs text-[#F5EFE4]/60 mt-1">Auto-renews monthly · Cancel anytime</p>
                    )}
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-[10px] uppercase tracking-[0.24em] text-[#C5A059]">Personal Code</p>
                  <div className="flex items-center gap-3 justify-center md:justify-end mt-2">
                    <p className="font-serif text-2xl tracking-wider" data-testid="dashboard-membership-code">{membership.membership_code}</p>
                    <button onClick={copyCode} className="p-2 text-[#F5EFE4]/70 hover:text-[#C5A059]" data-testid="dashboard-copy-code">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2 border-t border-white/10">
                {membership.cancel_at_period_end ? (
                  <button onClick={reactivateSub} className="luxor-btn-primary" data-testid="reactivate-membership-btn">
                    Reactivate renewal
                  </button>
                ) : (
                  <button onClick={cancelSub} className="text-xs uppercase tracking-[0.22em] text-[#F5EFE4]/70 hover:text-red-400 px-4 py-2 border border-white/15" data-testid="cancel-membership-btn">
                    Cancel membership
                  </button>
                )}
                <Link to="/membership" className="text-xs uppercase tracking-[0.22em] text-[#F5EFE4]/70 hover:text-[#C5A059] px-4 py-2 border border-white/15">
                  View plans
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white border border-[#E4D9C1] p-6 md:p-8 mb-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-testid="dashboard-join-cta">
              <div>
                <p className="overline">LSC Memberships</p>
                <h3 className="font-serif text-2xl text-[#1A1815] mt-1">Unlock discounts and priority.</h3>
                <p className="text-sm text-[#6B665E] mt-1">From £99/month · Cancel anytime.</p>
              </div>
              <Link to="/membership" className="luxor-btn-primary shrink-0">Explore memberships</Link>
            </div>
          )}
          <p className="overline mb-6">Your Reservations</p>
          {loading ? <p className="text-[#6B665E]">Loading…</p> : bookings.length === 0 ? (
            <div className="bg-white border border-[#E4D9C1] p-16 text-center">
              <p className="text-[#6B665E]">No reservations yet.</p>
              <Link to="/book" className="luxor-btn-outline-dark inline-flex mt-6">Make your first reservation</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {bookings.map((b) => (
                <div key={b.id} className="bg-white border border-[#E4D9C1] p-6 hover:border-[#C5A059] transition-colors" data-testid={`booking-card-${b.id}`}>
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0 flex-1"><p className="overline">{b.service_name}</p><h3 className="font-serif text-2xl mt-1 text-[#1A1815]">{b.package_name}</h3></div>
                    <div className="text-right">
                      <div className="font-serif text-2xl text-[#C5A059]">£{Number(b.price).toLocaleString()}</div>
                      <div className={`inline-block mt-2 text-[10px] uppercase tracking-[0.22em] px-2 py-1 border ${STATUS_STYLE[b.status] || "text-[#6B665E] border-black/20"}`}>{b.status}</div>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-3 text-xs">
                    <Field icon={CalendarDays} label="Date" value={b.date} />
                    <Field label="Time" value={b.time} />
                    <Field icon={CreditCard} label="Payment" value={PAYMENT_STATUS_LABEL[b.payment_status] || b.payment_status} />
                  </div>
                  {b.balance_due > 0 && b.payment_status === "deposit_paid" && (
                    <div className="mt-3 pt-3 border-t border-[#E4D9C1] text-xs" data-testid={`balance-due-${b.id}`}>
                      <div className="flex justify-between"><span className="text-[#6B665E]">Balance due on the day</span><span className="text-[#C5A059] font-medium">£{Number(b.balance_due).toFixed(2)}</span></div>
                    </div>
                  )}
                  <p className="mt-3 text-sm text-[#6B665E] flex items-start gap-2">
                    <MapPin size={13} className="mt-0.5 shrink-0 text-[#C5A059]" /><span className="line-clamp-2">{b.location}</span>
                  </p>
                  <Link
                    to={`/invoice/${b.id}`}
                    className="mt-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C5A059] hover:text-[#B08944]"
                    data-testid={`invoice-link-${b.id}`}
                  >
                    View invoice / receipt →
                  </Link>
                  {b.attachments && b.attachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#E4D9C1]" data-testid={`booking-attachments-${b.id}`}>
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#6B665E] flex items-center gap-1.5 mb-2">
                        <Paperclip size={11} className="text-[#C5A059]" /> {b.attachments.length} Attachment{b.attachments.length > 1 ? "s" : ""}
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {b.attachments.slice(0, 4).map((a, i) => (
                          a.resource_type === "image" ? (
                            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="block">
                              <img src={a.url} alt="" className="w-14 h-14 object-cover border border-[#E4D9C1] hover:border-[#C5A059] transition-colors" />
                            </a>
                          ) : (
                            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="w-14 h-14 flex items-center justify-center bg-[#F5EFE4] border border-[#E4D9C1] hover:border-[#C5A059] transition-colors">
                              <Paperclip size={16} className="text-[#C5A059]" />
                            </a>
                          )
                        ))}
                        {b.attachments.length > 4 && (
                          <span className="w-14 h-14 flex items-center justify-center text-xs text-[#6B665E] bg-[#F5EFE4] border border-[#E4D9C1]">
                            +{b.attachments.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ icon: I, label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B665E] flex items-center gap-1">{I ? <I size={11} className="text-[#C5A059]" /> : null} {label}</div>
      <div className="mt-1 text-[#1A1815] font-medium">{value}</div>
    </div>
  );
}
